import React, { useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { Link, useHistory } from 'react-router-dom';

import eventTracking from 'services/analytics/eventTracking';
import { t } from 'ttag';

import { Nullable } from '../../../types/general';
import { LoginFormFields } from './types';

import Alert from '../../ui/Alert/Alert';
import BackButton from '../../ui/BackButton/BackButton';
import Button from '../../ui/button/button';
import Checkbox from '../../ui/checkbox/checkbox';
import OutlinedInput from '../../ui/input/OutlinedInput/OutlinedInput';

import { useInterval } from '../../../lib/reactHelpers/hooks';
import {
    getLocationQuery,
    stringifyLocationQuery,
    updateQueryParamsPath,
} from '../../../lib/url';
import { getLocalStorageItem } from '../../../lib/webStorage';
import auth from '../../../services/auth';
import { TOO_MANY_REQUEST_ERROR_CODE } from '../../AuthCheckEmail/constants';
import FlexBox from '../../FlexBox';
import { LS_KEY_AUTH_INITIAL_FORM } from '../../auth/constants';
import PageTitle from '../../pageTitle/pageTitle';
import { ERROR_MESSAGE, HANDLED_ERRORS_BY_CODES } from '../constants';
import { useLoginSuccessEffect } from '../useLoginSuccessEffect';
import { loginFormResolver } from './validation';

import partnersLogo from '../../../images/auth/partners-mobile.png';

import css from './NewLoginForm.css';

const REQUEST_LOCK_DELAY = 60 * 60 * 1000; // one hour

const NewLoginForm: React.FC = () => {
    const [commonErrorMessage, setCommonErrorMessage] = useState<string>('');
    const [requestLockTime, setRequestLockTime] = useState<Nullable<number>>(
        null,
    );

    const history = useHistory();
    const searchQuery = getLocationQuery(history.location);

    const initialFormData = getLocalStorageItem(LS_KEY_AUTH_INITIAL_FORM);

    const {
        control,
        handleSubmit,
        formState,
        reset,
    } = useForm<LoginFormFields>({
        resolver: loginFormResolver,
        defaultValues: {
            password: '',
            remember: true,
        },
    });
    const loginSuccessEffect = useLoginSuccessEffect();

    const isDisabledLoginButton = formState.isSubmitting || !!requestLockTime;
    const isRequestLockTimeMoreMinute =
        !!requestLockTime && requestLockTime > 60;
    const requestLockTimeText = isRequestLockTimeMoreMinute
        ? t`годину`
        : t`${requestLockTime} с.`;

    const onSubmit: SubmitHandler<LoginFormFields> = async ({
        password,
        remember,
    }) => {
        try {
            setCommonErrorMessage('');
            const {
                next_url: nextUrl,
                is_2fa_enabled: is2faEnabled,
            } = await auth.login({
                email: initialFormData?.email,
                password,
                remember,
            });
            const url = updateQueryParamsPath(nextUrl, searchQuery);
            loginSuccessEffect(url, is2faEnabled);
            eventTracking.sendToGTM({
                event: 'funnel_reg_step_2_email',
                action: 'success',
                category: 'login',
            });
        } catch (error) {
            eventTracking.sendToGTM({
                event: 'funnel_reg_step_2_email',
                action: 'fail',
                category: 'login',
            });
            reset();
            const errorMessage = HANDLED_ERRORS_BY_CODES.includes(error.code)
                ? error.reason
                : ERROR_MESSAGE;

            if (error.code === TOO_MANY_REQUEST_ERROR_CODE) {
                setRequestLockTime(REQUEST_LOCK_DELAY);
                return;
            }

            setCommonErrorMessage(errorMessage);
        }
    };

    const onBackHandler = () => {
        history.push({
            pathname: '/auth/check-email',
            search: stringifyLocationQuery(searchQuery),
        });
    };

    useInterval(
        () => {
            if (!requestLockTime) {
                return;
            }
            setRequestLockTime(requestLockTime - 1);
        },
        requestLockTime ? 1000 : null,
    );

    useEffect(() => {
        if (!initialFormData?.email) {
            onBackHandler();
        }
    }, [initialFormData]);

    return (
        <form id="login" onSubmit={handleSubmit(onSubmit)}>
            <PageTitle>{t`Вхід`}</PageTitle>
            <FlexBox className={css.container} justify="center" gap={0}>
                <div className={css.content}>
                    <FlexBox className={css.header} direction="column" gap={27}>
                        <BackButton
                            className={css.backBtn}
                            onClick={onBackHandler}
                        />
                        <img
                            src={partnersLogo}
                            className={css.partnersLogos}
                            alt={t`Логотипи партнерів`}
                        />
                        <h1 className={css.title}>
                            <span
                                role="img"
                                aria-label={t`Раді вас бачити знову!`}
                            >
                                ✌️
                            </span>{' '}
                            {t`Раді вас бачити знову!`}
                        </h1>
                    </FlexBox>
                    <FlexBox direction="column" align="flex-start">
                        <input
                            className={css.hiddenInput}
                            id="email"
                            type="email"
                            value={initialFormData?.email}
                        />
                        <Controller
                            control={control}
                            name="password"
                            render={({ field, fieldState }) => (
                                <OutlinedInput
                                    id="password"
                                    autoComplete="current-password"
                                    type="password"
                                    name="password"
                                    required
                                    label={t`Пароль`}
                                    value={field.value}
                                    onChange={(event) =>
                                        field.onChange(
                                            event.target.value.trim(),
                                        )
                                    }
                                    autoFocus
                                    error={fieldState.error?.message}
                                />
                            )}
                        />
                        <Link
                            className={css.remindPasswordLink}
                            to={{
                                pathname: '/auth/password/remind',
                                search: stringifyLocationQuery(searchQuery),
                            }}
                        >
                            {t`Забули пароль?`}
                        </Link>
                    </FlexBox>
                    {!!requestLockTime && (
                        <Alert theme="error" hideIcon>
                            {t`Ви використали усі спроби на введення паролю. Спробуйте через ${requestLockTimeText}`}
                        </Alert>
                    )}
                    {commonErrorMessage && (
                        <Alert theme="error" hideIcon>
                            {commonErrorMessage}
                        </Alert>
                    )}
                    <div className={css.submitBtnContainer}>
                        <Button
                            className={css.submitBtn}
                            type="submit"
                            theme="darkGray"
                            isLoading={formState.isSubmitting}
                            disabled={isDisabledLoginButton}
                        >
                            {t`Увійти`}
                        </Button>
                        <Controller
                            control={control}
                            name="remember"
                            render={({ field }) => (
                                <Checkbox
                                    checked={field.value}
                                    onChange={field.onChange}
                                    color="deepSkyBlue"
                                    text={t`Запам’ятати мене`}
                                />
                            )}
                        />
                    </div>
                </div>
            </FlexBox>
        </form>
    );
};

export default NewLoginForm;
